import { useEffect, useRef, useCallback, useState } from 'react';
import { io } from 'socket.io-client';

/**
 * Socket.IO singleton — shared across all components
 * Lifecycle: connects on login, persists across navigation, disconnects on logout
 */
let socket = null;
let connectionAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 15;

/**
 * useSocket — core Socket.IO connection manager
 *
 * Features:
 * - Singleton connection (single socket shared across app)
 * - Auto-reconnect with exponential backoff
 * - JWT auth on handshake
 * - Room management (join/leave project/channel rooms)
 * - Presence tracking (online/away/DND)
 * - Typing indicators (throttled)
 *
 * Usage:
 *   const { socket, connected, emit, on, off, joinRoom, leaveRoom } = useSocket();
 */
export const useSocket = () => {
  const socketRef = useRef(socket);
  const [connected, setConnected] = useState(socket?.connected || false);

  useEffect(() => {
    const currentToken = localStorage.getItem('accessToken');
    if (!currentToken) return;

    // Create singleton connection
    if (!socket || socket.disconnected) {
      socket = io(window.location.origin, {
        auth: { token: currentToken },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 15000,
        timeout: 20000,
        forceNew: false,
      });

      socket.on('connect', () => {
        connectionAttempts = 0;
        setConnected(true);
        console.info('[Socket] Connected:', socket.id);
      });

      socket.on('disconnect', (reason) => {
        setConnected(false);
        console.info('[Socket] Disconnected:', reason);
        if (reason === 'io server disconnect') {
          setTimeout(() => socket?.connect(), 2000);
        }
      });

      socket.on('reconnect_attempt', (attempt) => {
        connectionAttempts = attempt;
        console.info(`[Socket] Reconnect attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS}`);
      });

      socket.on('reconnect', () => {
        connectionAttempts = 0;
        setConnected(true);
        console.info('[Socket] Reconnected');
      });

      socket.on('reconnect_failed', () => {
        console.error('[Socket] Reconnection failed after max attempts');
        setConnected(false);
      });

      socket.on('connect_error', (err) => {
        console.error('[Socket] Connection error:', err.message);
        if (err.message === 'Invalid token') {
          socket.disconnect();
        }
      });
    }

    socketRef.current = socket;

    return () => {
      // Keep connection alive across navigation — only disconnect on logout
    };
  }, []);

  // ── Core emit/on/off ──
  const emit = useCallback((event, data, ack) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data, ack);
    } else {
      console.warn(`[Socket] Cannot emit "${event}" — not connected`);
    }
  }, []);

  const on = useCallback((event, handler) => {
    socketRef.current?.on(event, handler);
    return () => socketRef.current?.off(event, handler);
  }, []);

  const off = useCallback((event, handler) => {
    socketRef.current?.off(event, handler);
  }, []);

  // ── Room management ──
  const joinRoom = useCallback((type, id) => {
    if (!id) return;
    emit(`join:${type}`, id);
  }, [emit]);

  const leaveRoom = useCallback((type, id) => {
    if (!id) return;
    emit(`leave:${type}`, id);
  }, [emit]);

  // ── Presence ──
  const setPresence = useCallback((status) => {
    emit('presence:update', { status });
  }, [emit]);

  // ── Typing indicators (throttled) ──
  const typingTimerRef = useRef(null);

  const sendTypingStart = useCallback((channelId) => {
    emit('hl:typing:start', { channelId });
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      emit('hl:typing:stop', { channelId });
    }, 3000);
  }, [emit]);

  const sendTypingStop = useCallback((channelId) => {
    clearTimeout(typingTimerRef.current);
    emit('hl:typing:stop', { channelId });
  }, [emit]);

  return {
    socket: socketRef.current,
    connected,
    emit,
    on,
    off,
    joinRoom,
    leaveRoom,
    setPresence,
    sendTypingStart,
    sendTypingStop,
  };
};

/**
 * Disconnect socket — called from logout flow
 */
export const disconnectSocket = () => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    connectionAttempts = 0;
  }
};

/**
 * Get raw socket instance (for non-hook contexts)
 */
export const getSocket = () => socket;
